'use client'

import React, {
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  ArrowRight,
  Award,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  ExternalLink,
  FileText,
  Files,
  ListFilter,
  Lock,
  Mail,
  MapPin,
  Megaphone,
  Phone,
  Sparkles,
  Tag,
  User,
} from 'lucide-react'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

import { ToggleBookmarkIconButton } from '@/components/bookmarks/ToggleBookmarkIconButton'
import { useCreateOpportunityChatSession } from '@/components/chat-v2/hooks/useChatSession'
import Button from '@/components/common/Button'
import { ResourcePermissionGate } from '@/components/common/PermissionGate'
import Spinner from '@/components/common/Spinner'
import {
  PursuitActions,
  buildFederalContractInput,
} from '@/components/opportunity/common/PursuitActionsV2'
import {
  RecipientLink,
  recipientHref,
} from '@/components/usaspending/RecipientLink'

import { cleanFileName } from '@/helpers/file'

import { useQuerySearchId } from '@/hooks/common/useQuerySearchId'
import { useRenderNowMs } from '@/hooks/common/useRenderNowMs'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import { selectRecipientLevel } from '@/lib/usaspending/recipientId'

import type {
  FederalContract,
  FederalNotice,
  LinkedAwardsResponse,
} from '@/types/federal-contract/FederalContract'
import { isUUID } from '@/types/Id'
import type { SearchOut } from '@/types/Search'

import {
  PageTitleV2,
  ShareIcon,
  SimilarOpportunitiesV2,
  ViewDocumentsButton,
} from '../../common'
import { BadgeV2 } from '../../common/BadgeV2'
import { CardV2 } from '../../common/CardV2'
import { ContentPanelV2 } from '../../common/ContentPanelV2'
import { DataBlockV2 } from '../../common/DataBlockV2'
import {
  type FederalContractDiffs,
  useDaysRemaining,
  useFederalContractDiffs,
  useNoticeScrollHighlight,
} from '../../common/hooks'
import { NoticesV2, useCardContext } from '../../common/NoticesV2'
import { SidebarWidgetV2 } from '../../common/SidebarWidgetV2'
import { TabPanelV2 } from '../../common/TabPanelV2'
import { formatCurrency, formatDate } from '../../common/transforms'
import {
  SolicitationDocumentsSection,
  SolicitationDocumentsTab,
} from './SolicitationDocumentsSection'
import {
  type UnifiedAwardNotice,
  combineAwardNotices,
  extendTimelineWithLinkedAwards,
  getNoticeTypeVariant,
  normalizeContractData,
  normalizeLinkedAwards,
} from './transforms'

// ============================================================================
// Types
// ============================================================================

const federalContractKpiGridClassName =
  'mb-4 grid grid-cols-2 gap-2 md:mb-6 md:grid-cols-4 md:gap-3'

type DocumentsTabEntrySource = 'default' | 'compact-file-click'

interface ContractDisplayV2Props {
  contract: FederalContract
  linkedAwardsData?: LinkedAwardsResponse | null
  isLoadingAwards?: boolean
  similarOpportunities?: SearchOut[]
  isSimilarLoading?: boolean
  onShare?: () => void
}

// ============================================================================
// ContractNoticeCard - Enhanced notice card content for NoticesV2
// ============================================================================

interface ContractNoticeCardProps {
  notice: FederalNotice
  /** Previous notice for comparison (undefined for original notice) */
  previousNotice?: FederalNotice
  /** Diff data for this notice (undefined for original notice) */
  diffs?: FederalContractDiffs
  /** Whether this notice is an amendment */
  isAmendment: boolean
  /** When true, renders a compact version with fewer description lines */
  isCompact?: boolean
}

/**
 * Determines if a notice is an amendment based on notice type.
 * An amendment is a notice that has the same type as a previous (older) notice,
 * indicating it's an update rather than a lifecycle progression (upgrade).
 *
 * Notices are sorted newest-first, so "previous" means higher index.
 *
 * @param notices - All notices (sorted newest-first)
 * @param idx - Current index in the notices array
 * @returns true if this is an amendment (same type as a previous notice)
 */
function isAmendment(notices: FederalNotice[], idx: number): boolean {
  // Single notice = original, not an amendment
  if (notices.length === 1) return false

  // Last notice (oldest) is always the original
  if (idx === notices.length - 1) return false

  const currentType = notices[idx].notice_type

  // Check if any older notice (higher index) has the same type
  for (let i = idx + 1; i < notices.length; i++) {
    if (notices[i].notice_type === currentType) {
      return true // Same type as a previous notice = amendment
    }
  }

  // Different type from all previous notices = upgrade/new stage
  return false
}

/**
 * Pure helper to generate amendment title with optional description badge.
 * Extracted to avoid inline JSX in props.
 */
function getAmendmentTitle(
  isLatest: boolean,
  amendmentNum: number,
  descriptionChanged: boolean,
): ReactNode {
  const label = isLatest ? 'Latest Amendment' : `Amendment #${amendmentNum}`
  if (!descriptionChanged) return label
  return (
    <span>
      {label}
      <span className="ml-1.5 text-sm font-normal text-amber-600 dark:text-amber-500">
        · Description Updated
      </span>
    </span>
  )
}

/**
 * ContractNoticeCard - Pure memoized component for notice card content
 * Follows rerender-memo pattern: extracts expensive rendering into memoized component
 */
const ContractNoticeCard = memo(function ContractNoticeCard({
  notice,
  previousNotice,
  diffs,
  isAmendment,
  isCompact = false,
}: ContractNoticeCardProps) {
  const { isExpanded } = useCardContext()
  const hasDescription = Boolean(notice.description)

  // Show metadata when card is not compact OR when description is expanded
  const showMetadata = !isCompact || isExpanded

  return (
    <>
      {/* Header Section with Icon + Label */}
      <NoticesV2.Card.HeaderSection>
        <NoticesV2.Card.Icon />
        <NoticesV2.Card.Label />
        <NoticesV2.Card.HeaderContent>
          <NoticesV2.Card.Title>
            {notice.title || 'Untitled Notice'}
          </NoticesV2.Card.Title>
          <NoticesV2.Card.Meta>
            {formatDate(notice.posted_date)}
            {notice.notice_type && ` · ${notice.notice_type.toUpperCase()}`}
            {isAmendment && diffs?.descriptionChanged ? (
              <span className="ml-1.5 inline-flex -translate-y-px items-center rounded bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-medium leading-none text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 sm:ml-2 sm:translate-y-0 sm:text-sm">
                Updated
              </span>
            ) : null}
          </NoticesV2.Card.Meta>
        </NoticesV2.Card.HeaderContent>
        <NoticesV2.Card.CurrentBadge />
      </NoticesV2.Card.HeaderSection>

      {/* Body Section */}
      <NoticesV2.Card.BodySection>
        {hasDescription ? (
          <NoticesV2.Card.Description
            markdown
            lines={isCompact ? 2 : 4}
            className={
              isCompact ? 'text-primary-500 dark:text-primary-400' : undefined
            }
          >
            {notice.description}
          </NoticesV2.Card.Description>
        ) : (
          <p className="text-sm italic text-primary-400 dark:text-primary-500">
            No description provided.
          </p>
        )}

        {/* Metadata Grid - show when not compact OR when expanded */}
        {showMetadata && (
          <NoticesV2.Card.MetadataGrid>
            <NoticesV2.Card.MetadataItem
              label="Response Deadline"
              value={
                diffs?.deadlineChanged && previousNotice?.response_deadline ? (
                  <span className="font-semibold text-amber-700 dark:text-amber-400">
                    {formatDate(notice.response_deadline)}
                    <span className="ml-1 text-sm font-normal text-primary-500">
                      (was {formatDate(previousNotice.response_deadline)})
                    </span>
                  </span>
                ) : notice.response_deadline ? (
                  formatDate(notice.response_deadline)
                ) : null
              }
            />
            <NoticesV2.Card.MetadataItem
              label="Contact"
              value={notice.primary_contact_full_name}
            />
          </NoticesV2.Card.MetadataGrid>
        )}
      </NoticesV2.Card.BodySection>
    </>
  )
})

// ============================================================================
// AwardCard - Compound component for displaying award information
// ============================================================================

// Sub-component types
type AwardSource = 'usaspending' | 'sam'

interface AwardCardRootProps {
  children: React.ReactNode
}

interface AwardCardContentProps {
  children: React.ReactNode
}

interface AwardCardHeaderProps {
  children: React.ReactNode
}

interface AwardCardIconProps {
  source: AwardSource
}

interface AwardCardTitleProps {
  children: React.ReactNode
  href?: string
}

interface AwardCardSourceBadgeProps {
  source: AwardSource
}

interface AwardCardTypeBadgeProps {
  children: React.ReactNode
}

interface AwardCardRecipientProps {
  name: string
  href?: string | null
}

interface AwardCardMetaProps {
  children: React.ReactNode
}

interface AwardCardActionsProps {
  children: React.ReactNode
}

// Pure sub-components
const AwardCardRoot: React.FC<AwardCardRootProps> = ({ children }) => (
  <div className="flex items-start justify-between gap-4">{children}</div>
)

const AwardCardContent: React.FC<AwardCardContentProps> = ({ children }) => (
  <div className="flex-1 space-y-2">{children}</div>
)

const AwardCardHeader: React.FC<AwardCardHeaderProps> = ({ children }) => (
  <div className="flex items-center gap-2">{children}</div>
)

const AwardCardIcon: React.FC<AwardCardIconProps> = ({ source }) => {
  const colorClass =
    source === 'usaspending' ? 'text-accent-500' : 'text-success-500'
  return <Award className={`h-4 w-4 ${colorClass}`} />
}

const AwardCardTitle: React.FC<AwardCardTitleProps> = ({ children, href }) => {
  if (href) {
    return (
      <Link
        href={href}
        className="text-sm font-medium text-primary-900 hover:text-blue-400 hover:underline dark:text-primary-100 dark:hover:text-blue-400"
      >
        {children}
      </Link>
    )
  }
  return (
    <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
      {children}
    </span>
  )
}

const AwardCardSourceBadge: React.FC<AwardCardSourceBadgeProps> = ({
  source,
}) => {
  const variant = source === 'usaspending' ? 'info' : 'success'
  const label = source === 'usaspending' ? 'USAspending' : 'SAM.gov'
  return (
    <BadgeV2 variant={variant} size="sm">
      {label}
    </BadgeV2>
  )
}

const AwardCardTypeBadge: React.FC<AwardCardTypeBadgeProps> = ({
  children,
}) => (
  <BadgeV2 variant="outline" size="sm">
    {children}
  </BadgeV2>
)

const AwardCardRecipient: React.FC<AwardCardRecipientProps> = ({
  name,
  href,
}) => (
  <div className="text-sm text-primary-600 dark:text-primary-400">
    Awarded to:{' '}
    {href ? (
      <Link
        href={href}
        prefetch={false}
        className="font-medium hover:text-blue-400 hover:underline dark:hover:text-blue-400"
      >
        {name}
      </Link>
    ) : (
      <span className="font-medium">{name}</span>
    )}
  </div>
)

const AwardCardMeta: React.FC<AwardCardMetaProps> = ({ children }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-primary-500 dark:text-primary-400">
    {children}
  </div>
)

const AwardCardActions: React.FC<AwardCardActionsProps> = ({ children }) => (
  <div>{children}</div>
)

// Compound component namespace
const AwardCard = {
  Root: AwardCardRoot,
  Content: AwardCardContent,
  Header: AwardCardHeader,
  Icon: AwardCardIcon,
  Title: AwardCardTitle,
  SourceBadge: AwardCardSourceBadge,
  TypeBadge: AwardCardTypeBadge,
  Recipient: AwardCardRecipient,
  Meta: AwardCardMeta,
  Actions: AwardCardActions,
}

// ============================================================================
// Composed Award Cards using AwardCard compound component
// ============================================================================

interface LinkedAwardCardProps {
  award: {
    id: number
    generatedId: string
    piid: string | null
    typeDescription: string | null
    totalObligation: number | null
    recipientName: string | null
    recipientHash: string | null
    recipientLevels: string[]
    parentUei: string | null
    agencyName: string | null
    actionDate: string | null
    dateSigned: string | null
  }
}

const LinkedAwardCard: React.FC<LinkedAwardCardProps> = ({ award }) => {
  const awardUrl = `/awards/usaspending/${award.generatedId}`
  const recipientUrl = recipientHref(
    award.recipientHash,
    selectRecipientLevel(award.recipientLevels, award.parentUei),
  )
  const displayDate = award.dateSigned || award.actionDate

  return (
    <AwardCard.Root>
      <AwardCard.Content>
        <AwardCard.Header>
          <AwardCard.Icon source="usaspending" />
          <AwardCard.Title href={awardUrl}>
            {award.piid || `Award #${award.id}`}
          </AwardCard.Title>
          <AwardCard.SourceBadge source="usaspending" />
          {award.typeDescription && (
            <AwardCard.TypeBadge>{award.typeDescription}</AwardCard.TypeBadge>
          )}
        </AwardCard.Header>
        {award.recipientName && (
          <AwardCard.Recipient name={award.recipientName} href={recipientUrl} />
        )}
        <AwardCard.Meta>
          {award.totalObligation != null && (
            <span>{formatCurrency(award.totalObligation)}</span>
          )}
          {displayDate && <span>{formatDate(displayDate)}</span>}
          {award.agencyName && <span>{award.agencyName}</span>}
        </AwardCard.Meta>
      </AwardCard.Content>
      <AwardCard.Actions>
        <Link href={awardUrl}>
          <Button variant="outline-compact" className="text-sm">
            View Award
            <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </Link>
      </AwardCard.Actions>
    </AwardCard.Root>
  )
}

interface SamAwardNoticeCardProps {
  award: {
    id: string
    awardNumber: string | null
    awardDate: string | null
    awardAmount: number | null
    awardee: string | null
  }
  /** When true, renders a compact/de-emphasized version */
  compact?: boolean
}

const SamAwardNoticeCard: React.FC<SamAwardNoticeCardProps> = ({
  award,
  compact = false,
}) => {
  if (compact) {
    // Compact variant - single line, de-emphasized
    return (
      <div className="flex items-center justify-between gap-4 py-1 text-sm text-primary-500 dark:text-primary-400">
        <div className="flex items-center gap-2">
          <Award className="h-3 w-3 text-primary-400" />
          <span>{award.awardNumber || 'Award Notice'}</span>
          <BadgeV2 variant="neutral" size="sm">
            SAM.gov
          </BadgeV2>
          {award.awardee && (
            <span className="text-primary-400">→ {award.awardee}</span>
          )}
        </div>
        <div className="flex items-center gap-3 tabular-nums">
          {award.awardAmount != null && (
            <span>{formatCurrency(award.awardAmount)}</span>
          )}
          {award.awardDate && <span>{formatDate(award.awardDate)}</span>}
        </div>
      </div>
    )
  }

  // Full variant - same structure as LinkedAwardCard
  return (
    <AwardCard.Root>
      <AwardCard.Content>
        <AwardCard.Header>
          <AwardCard.Icon source="sam" />
          <AwardCard.Title>
            {award.awardNumber || 'Award Notice'}
          </AwardCard.Title>
          <AwardCard.SourceBadge source="sam" />
        </AwardCard.Header>
        {award.awardee && <AwardCard.Recipient name={award.awardee} />}
        <AwardCard.Meta>
          {award.awardAmount != null && (
            <span>{formatCurrency(award.awardAmount)}</span>
          )}
          {award.awardDate && <span>{formatDate(award.awardDate)}</span>}
        </AwardCard.Meta>
      </AwardCard.Content>
    </AwardCard.Root>
  )
}

// ============================================================================
// Main Component
// ============================================================================

const ContractDisplayV2: React.FC<ContractDisplayV2Props> = ({
  contract,
  linkedAwardsData,
  isLoadingAwards = false,
  similarOpportunities = [],
  isSimilarLoading = false,
  onShare,
}) => {
  const { searchId } = useQuerySearchId()
  const { activeOrgId } = useActiveOrgId()
  const { safePush } = useSafePush()
  const referenceNowMs = useRenderNowMs({ refreshMs: 60_000 })
  const {
    mutateAsync: createOpportunityChatSession,
    isPending: isCreatingOpportunityChatSession,
  } = useCreateOpportunityChatSession()

  // Memoize normalized data at the source level - contract.id is stable
  const data = useMemo(
    () => normalizeContractData(contract, referenceNowMs ?? Date.now()),
    [contract, referenceNowMs],
  )
  const solicitationFiles = data.files

  // Use headless hook for diff computation
  const { diffsByNoticeId, latestDiffs } = useFederalContractDiffs(data.notices)

  // Derive deadline highlight state (subscribe to derived boolean, not raw values)
  const deadlineHighlighted = latestDiffs?.deadlineChanged ?? false

  // Memoize linked awards - linkedAwardsData is stable from react-query
  const linkedAwards = useMemo(
    () => normalizeLinkedAwards(linkedAwardsData),
    [linkedAwardsData],
  )

  // Derive unified award notices - now has stable deps
  const unifiedAwardNotices = useMemo(
    () => combineAwardNotices(data.awardNotices, linkedAwards),
    [data, linkedAwards],
  )

  // Derive extended timeline - now has stable deps
  const extendedTimeline = useMemo(
    () =>
      extendTimelineWithLinkedAwards(
        data.timeline,
        linkedAwards,
        referenceNowMs ?? Date.now(),
      ),
    [data, linkedAwards, referenceNowMs],
  )

  // Build opportunity input for pursuit actions
  const opportunityInput = useMemo(
    () =>
      buildFederalContractInput(
        {
          federal_contract_id: data.latestNotice.federal_contract_id,
          title: data.title,
          response_deadline: data.latestNotice.response_deadline,
        },
        data.aiSummary,
      ),
    [
      data.latestNotice.federal_contract_id,
      data.title,
      data.latestNotice.response_deadline,
      data.aiSummary,
    ],
  )

  const [activeTab, setActiveTab] = useState('analysis')
  const [documentsTabEntrySource, setDocumentsTabEntrySource] =
    useState<DocumentsTabEntrySource>('default')
  const [selectedSolicitationFileId, setSelectedSolicitationFileId] = useState<
    string | null
  >(null)
  const [
    solicitationFileScrollRequestKey,
    setSolicitationFileScrollRequestKey,
  ] = useState<number | null>(null)
  const isDocumentsTab = activeTab === 'documents'

  const handleActiveTabChange = useCallback((nextTab: string) => {
    if (nextTab === 'documents') {
      setDocumentsTabEntrySource('default')
    }
    setActiveTab(nextTab)
  }, [])

  // Stable callbacks for timeline event clicks
  const goToHistoryTab = useCallback(() => setActiveTab('history'), [])
  const goToAwardsTab = useCallback(() => setActiveTab('awards'), [])

  // Notice scroll-highlight for timeline click interactions
  const { highlightedNoticeId, scrollToNotice } = useNoticeScrollHighlight()

  // Ref to track pending timeout for cleanup
  const goToNoticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const goToDocumentsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (goToNoticeTimeoutRef.current) {
        clearTimeout(goToNoticeTimeoutRef.current)
      }
      if (goToDocumentsTimeoutRef.current) {
        clearTimeout(goToDocumentsTimeoutRef.current)
      }
    }
  }, [])

  // Navigate to history tab and scroll to specific notice
  const goToNotice = useCallback(
    (noticeId: string) => {
      setActiveTab('history')
      // Clear any pending timeout
      if (goToNoticeTimeoutRef.current) {
        clearTimeout(goToNoticeTimeoutRef.current)
      }
      // Small delay to let tab switch render
      goToNoticeTimeoutRef.current = setTimeout(
        () => scrollToNotice(noticeId),
        100,
      )
    },
    [scrollToNotice],
  )

  const goToDocumentsTab = useCallback(
    (fileId?: string | null, source: DocumentsTabEntrySource = 'default') => {
      setDocumentsTabEntrySource(source)
      setActiveTab('documents')
      if (fileId) {
        setSelectedSolicitationFileId(fileId)
        setSolicitationFileScrollRequestKey(Date.now())
      }

      if (goToDocumentsTimeoutRef.current) {
        clearTimeout(goToDocumentsTimeoutRef.current)
      }

      goToDocumentsTimeoutRef.current = setTimeout(() => {
        document
          .getElementById('solicitation-documents-tab-panel')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    },
    [],
  )

  const analyzeSolicitationFile = useCallback(
    async (file: { name: string }) => {
      if (!isUUID(activeOrgId)) return

      const session = await createOpportunityChatSession({
        opportunity_id: data.latestNotice.federal_contract_id,
        opportunity_type: 'federal_contract',
        organization_id: activeOrgId,
        title: `${data.title}: ${cleanFileName(file.name)}`,
      })

      if (session) {
        safePush(`/orgs/${activeOrgId}/c/opp/${session.id}`)
      }
    },
    [
      activeOrgId,
      createOpportunityChatSession,
      data.latestNotice.federal_contract_id,
      data.title,
      safePush,
    ],
  )

  // Track if we've completed loading awards at least once
  const hasFinishedLoadingOnce = useRef(false)

  // Use shared hooks - always use latest notice for data display
  const {
    daysRemaining,
    isPast: isPastDeadline,
    date: responseDeadline,
  } = useDaysRemaining(data.latestNotice.response_deadline)

  // Check if contract has awards - uses unified notices for consistent state
  const hasAwards = unifiedAwardNotices.length > 0

  // Memoize amendments list computation for timeline
  const amendments = useMemo(
    () =>
      data.notices
        .slice(0, -1)
        .map((notice, idx) => ({ notice, idx }))
        .filter(({ idx }) => isAmendment(data.notices, idx))
        .filter(({ notice }) => notice.posted_date)
        .reverse(),
    [data.notices],
  )

  // Determine primary award source and derive display data
  // Priority: USAspending (authoritative) > SAM.gov (announcements)
  const hasUsaspendingAwards = linkedAwards && linkedAwards.awardCount > 0
  const hasSamAwards = data.awardNotices.length > 0

  // Primary award data for header blocks
  const primaryAwardData = useMemo(() => {
    if (hasUsaspendingAwards && linkedAwards) {
      // USAspending is primary source. Sort by actionDate (latest modification)
      // to find the most-recently-modified award; dateSigned is fixed at award
      // creation and doesn't reflect recency. We then display dateSigned for
      // that award (see `date` below) so the header shows the original signing date.
      const sortedAwards = [...linkedAwards.awards].sort((a, b) => {
        if (!a.actionDate && !b.actionDate) return 0
        if (!a.actionDate) return 1
        if (!b.actionDate) return -1
        return (
          new Date(b.actionDate).getTime() - new Date(a.actionDate).getTime()
        )
      })
      const latestAward = sortedAwards[0]
      return {
        source: 'usaspending' as const,
        totalObligated: linkedAwards.totalObligated,
        latestAward: {
          id: latestAward.generatedId,
          awardee: latestAward.recipientName,
          amount: latestAward.totalObligation,
          date: latestAward.dateSigned || latestAward.actionDate,
          href: `/awards/usaspending/${latestAward.generatedId}`,
          recipientHref: recipientHref(
            latestAward.recipientHash,
            selectRecipientLevel(
              latestAward.recipientLevels,
              latestAward.parentUei,
            ),
          ),
        },
      }
    }

    if (hasSamAwards) {
      // SAM.gov as fallback - sum all award amounts
      const totalAmount = data.awardNotices.reduce(
        (sum, a) => sum + (a.awardAmount ?? 0),
        0,
      )
      // Find latest by date
      const sorted = [...data.awardNotices].sort((a, b) => {
        if (!a.awardDate && !b.awardDate) return 0
        if (!a.awardDate) return 1
        if (!b.awardDate) return -1
        return new Date(b.awardDate).getTime() - new Date(a.awardDate).getTime()
      })
      const latestAward = sorted[0]
      return {
        source: 'sam' as const,
        totalObligated: totalAmount > 0 ? totalAmount : null,
        latestAward: {
          id: latestAward.id,
          awardee: latestAward.awardee,
          amount: latestAward.awardAmount,
          date: latestAward.awardDate,
          href: null, // SAM.gov awards don't have detail pages
          recipientHref: null, // SAM.gov awards don't have recipient pages
        },
      }
    }

    return null
  }, [hasUsaspendingAwards, linkedAwards, hasSamAwards, data.awardNotices])

  // Should we show award data in header blocks instead of deadline?
  const showAwardDataInHeader = isPastDeadline && hasAwards && primaryAwardData

  // Handle awards tab state when loading completes
  // If user is on awards tab and awards finish loading with no results,
  // switch back to the default tab to avoid invalid state
  useEffect(() => {
    if (!isLoadingAwards) {
      hasFinishedLoadingOnce.current = true
      // If we're on the awards tab but there are no awards, switch back
      if (activeTab === 'awards' && !hasAwards) {
        setActiveTab('analysis')
      }
    }
  }, [isLoadingAwards, hasAwards, activeTab])

  return (
    <>
      {/* Header Block */}
      <PageTitleV2>
        <PageTitleV2.Badges>
          {data.badges.map((badge, idx) => (
            <BadgeV2
              key={`${badge.variant}-${badge.label}`}
              variant={idx === 0 && hasAwards ? 'success' : badge.variant}
            >
              {idx === 0 && hasAwards ? 'Awarded Contract' : badge.label}
            </BadgeV2>
          ))}
        </PageTitleV2.Badges>
        <PageTitleV2.Content className="lg:max-w-full">
          <PageTitleV2.Title>{data.title}</PageTitleV2.Title>
        </PageTitleV2.Content>
        <PageTitleV2.Row className="lg:flex-row lg:items-center lg:justify-between lg:gap-4">
          <PageTitleV2.Content>
            <PageTitleV2.Subtitle icon={Building2}>
              {data.subtitle}
              {data.solicitationNumber && (
                <span className="inline-flex items-center gap-1 text-primary-500 dark:text-primary-400">
                  {`\u2022`}&nbsp;
                  <PageTitleV2.Meta>{data.solicitationNumber}</PageTitleV2.Meta>
                </span>
              )}
            </PageTitleV2.Subtitle>
          </PageTitleV2.Content>
          <PageTitleV2.Actions className="lg:w-auto lg:flex-nowrap">
            <ToggleBookmarkIconButton
              bookmarkableType="federal_contract"
              objectId={contract.id}
              iconSize={16}
              className="h-7 w-7 border border-primary-300 dark:border-primary-700 rounded-md bg-white dark:bg-transparent"
            />
            <Button variant="outline-compact" onClick={onShare}>
              <ShareIcon />
              Share
            </Button>
            {isUUID(activeOrgId) ? (
              <ResourcePermissionGate
                resourceType="organization"
                resourceId={activeOrgId}
                permission="write"
                fallback="hide"
              >
                <PursuitActions.Root
                  opportunity={opportunityInput}
                  searchId={searchId}
                >
                  <PursuitActions.BeginResponseButton className="w-full max-md:h-10 md:w-auto" />
                  <PursuitActions.AskAiButton className="w-full max-md:h-10 md:w-auto" />
                  <PursuitActions.CreatePursuitButton
                    variant="primary-compact"
                    label="Pursue"
                    loadingLabel="Pursuing..."
                    successLabel="Pursuing"
                    className="w-full max-md:h-10 md:w-auto"
                  />
                </PursuitActions.Root>
              </ResourcePermissionGate>
            ) : null}
          </PageTitleV2.Actions>
        </PageTitleV2.Row>
      </PageTitleV2>

      {/* Data Grid */}
      <div className={federalContractKpiGridClassName}>
        {showAwardDataInHeader ? (
          // Award data blocks - shown when deadline passed and awards exist
          <>
            <DataBlockV2>
              <DataBlockV2.Header>
                <DataBlockV2.Icon icon={DollarSign} color="success" />
                <DataBlockV2.Label>Total Obligated</DataBlockV2.Label>
              </DataBlockV2.Header>
              <DataBlockV2.Value>
                {primaryAwardData.totalObligated != null
                  ? formatCurrency(primaryAwardData.totalObligated)
                  : '—'}
              </DataBlockV2.Value>
              <DataBlockV2.SubValue>
                {primaryAwardData.source === 'usaspending'
                  ? 'USAspending.gov'
                  : 'SAM.gov'}
              </DataBlockV2.SubValue>
            </DataBlockV2>
            <DataBlockV2>
              <DataBlockV2.Header>
                <DataBlockV2.Icon icon={Award} color="accent" />
                <DataBlockV2.Label>Current Recipient</DataBlockV2.Label>
              </DataBlockV2.Header>
              <DataBlockV2.Value className="text-sm">
                {primaryAwardData.latestAward.recipientHref ? (
                  <Link
                    href={primaryAwardData.latestAward.recipientHref}
                    className="hover:text-blue-400 hover:underline dark:hover:text-blue-400"
                  >
                    {primaryAwardData.latestAward.awardee || 'Awarded'}
                  </Link>
                ) : (
                  primaryAwardData.latestAward.awardee || 'Awarded'
                )}
              </DataBlockV2.Value>
              {primaryAwardData.latestAward.date && (
                <DataBlockV2.SubValue>
                  {formatDate(primaryAwardData.latestAward.date)}
                </DataBlockV2.SubValue>
              )}
            </DataBlockV2>
          </>
        ) : (
          // Deadline blocks - default when solicitation is active
          <>
            <DataBlockV2>
              <DataBlockV2.Header>
                <DataBlockV2.Icon icon={Calendar} color="warning" />
                <DataBlockV2.Label>Response Deadline</DataBlockV2.Label>
              </DataBlockV2.Header>
              <DataBlockV2.Value>
                {data.stats.responseDeadline ?? '—'}
              </DataBlockV2.Value>
              {(isPastDeadline || daysRemaining) && (
                <DataBlockV2.SubValue>
                  {isPastDeadline
                    ? 'Deadline passed'
                    : `${daysRemaining} days left`}
                </DataBlockV2.SubValue>
              )}
            </DataBlockV2>
            <DataBlockV2>
              <DataBlockV2.Header>
                <DataBlockV2.Icon icon={Clock} color="accent" />
                <DataBlockV2.Label>Days Remaining</DataBlockV2.Label>
              </DataBlockV2.Header>
              <DataBlockV2.Value>
                {daysRemaining !== null
                  ? isPastDeadline
                    ? '0'
                    : daysRemaining
                  : '—'}
              </DataBlockV2.Value>
              <DataBlockV2.SubValue>
                {isPastDeadline ? 'Closed' : 'Until deadline'}
              </DataBlockV2.SubValue>
            </DataBlockV2>
          </>
        )}
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={Briefcase} color="success" />
            <DataBlockV2.Label>Set-Aside</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value
            title={data.stats.setAsideDescription || undefined}
            className="text-sm"
          >
            {data.stats.setAsideDescription ||
              data.stats.setAsideCode ||
              'Full & Open'}
          </DataBlockV2.Value>
        </DataBlockV2>
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={FileText} color="info" />
            <DataBlockV2.Label>Notice Type</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value className="text-sm">
            {data.stats.noticeType || '—'}
          </DataBlockV2.Value>
        </DataBlockV2>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
        {/* Main Content Column */}
        <div
          className={twMerge(
            'flex flex-col gap-4',
            isDocumentsTab ? 'lg:col-span-12' : 'lg:col-span-8',
          )}
        >
          {/* Tabs */}
          <TabPanelV2
            value={activeTab}
            onValueChange={handleActiveTabChange}
            defaultTab="analysis"
          >
            <TabPanelV2.List>
              <TabPanelV2.Trigger value="analysis">
                <span className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  AI Analysis
                </span>
              </TabPanelV2.Trigger>
              <TabPanelV2.Trigger value="history">
                <span className="flex items-center gap-1.5">
                  <ListFilter className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">
                    {data.notices.length === 1
                      ? 'Solicitation Details'
                      : `Solicitation History (${data.notices.length})`}
                  </span>
                  <span className="sm:hidden">
                    {data.notices.length === 1
                      ? 'Details'
                      : `History (${data.notices.length})`}
                  </span>
                </span>
              </TabPanelV2.Trigger>
              <TabPanelV2.Trigger value="documents">
                <span className="flex items-center gap-1.5">
                  <Files className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">
                    Solicitation Documents ({solicitationFiles.length})
                  </span>
                  <span className="sm:hidden">
                    Documents ({solicitationFiles.length})
                  </span>
                </span>
              </TabPanelV2.Trigger>
              {(hasAwards || isLoadingAwards) && (
                <TabPanelV2.Trigger value="awards">
                  <span className="flex items-center gap-1">
                    Awards
                    <span className="inline-flex h-4 w-7 items-center justify-center">
                      {isLoadingAwards && unifiedAwardNotices.length === 0 ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : unifiedAwardNotices.length > 0 ? (
                        <span className="tabular-nums">
                          ({unifiedAwardNotices.length})
                        </span>
                      ) : null}
                    </span>
                  </span>
                </TabPanelV2.Trigger>
              )}
            </TabPanelV2.List>

            {/* Analysis Panel - card container */}
            <TabPanelV2.Content value="analysis">
              <CardV2 variant="accent" className="mt-2">
                <CardV2.Header>
                  <CardV2.HeaderIcon>
                    <Sparkles />
                  </CardV2.HeaderIcon>
                  <CardV2.HeaderTitle>
                    Contract Opportunity Analysis
                  </CardV2.HeaderTitle>
                </CardV2.Header>
                <CardV2.Content>
                  <ContentPanelV2 variant="inline">
                    <ContentPanelV2.Content markdown>
                      {data.aiSummary ||
                        data.description ||
                        'No summary available.'}
                    </ContentPanelV2.Content>
                    <ContentPanelV2.Footer>
                      <ContentPanelV2.CopyButton />
                    </ContentPanelV2.Footer>
                  </ContentPanelV2>
                </CardV2.Content>
              </CardV2>
            </TabPanelV2.Content>

            {/* History Panel - timeline with card-styled notice cards */}
            {/* Reversed order to mirror sidebar timeline (oldest first) */}
            <TabPanelV2.Content value="history" className="mt-2">
              <NoticesV2>
                <NoticesV2.Timeline>
                  {[...data.notices].reverse().map((notice, idx, reversed) => {
                    // Original index in the non-reversed array
                    const originalIdx = data.notices.length - 1 - idx
                    const isAmend = isAmendment(data.notices, originalIdx)
                    const previousNotice = data.notices[originalIdx + 1]
                    const diffs = diffsByNoticeId.get(notice.id)
                    const isCurrentNotice = notice.id === data.latestNotice.id

                    return (
                      <NoticesV2.Card
                        key={notice.id}
                        id={notice.id}
                        isLatest={isCurrentNotice}
                        noticeType={isAmend ? 'amendment' : 'original'}
                        isHighlighted={highlightedNoticeId === notice.id}
                        showConnector
                        isFirst={idx === 0}
                        isLast={idx === reversed.length - 1}
                      >
                        <ContractNoticeCard
                          notice={notice}
                          previousNotice={previousNotice}
                          diffs={diffs}
                          isAmendment={isAmend}
                          isCompact={!isCurrentNotice}
                        />
                      </NoticesV2.Card>
                    )
                  })}
                </NoticesV2.Timeline>
              </NoticesV2>
            </TabPanelV2.Content>

            <TabPanelV2.Content value="documents" className="mt-2">
              <SolicitationDocumentsTab
                files={solicitationFiles}
                persistenceKey={data.latestNotice.federal_contract_id}
                selectedFileId={selectedSolicitationFileId}
                onSelectedFileIdChange={setSelectedSolicitationFileId}
                selectionScrollRequestKey={solicitationFileScrollRequestKey}
                onSelectionScrollRequestHandled={() =>
                  setSolicitationFileScrollRequestKey(null)
                }
                suppressDefaultListOpen={
                  documentsTabEntrySource === 'compact-file-click'
                }
                onAnalyzeFile={analyzeSolicitationFile}
                isAnalyzingFile={isCreatingOpportunityChatSession}
              />
            </TabPanelV2.Content>

            {/* Awards Panel */}
            {(hasAwards || isLoadingAwards) && (
              <TabPanelV2.Content value="awards">
                <CardV2 variant="accent" className="mt-2">
                  <CardV2.Header>
                    <CardV2.HeaderIcon>
                      <Award />
                    </CardV2.HeaderIcon>
                    <CardV2.HeaderTitle>Linked Awards</CardV2.HeaderTitle>
                  </CardV2.Header>
                  <CardV2.Content>
                    {/* Summary header - uses primary award source */}
                    {primaryAwardData &&
                      primaryAwardData.totalObligated != null && (
                        <div className="flex items-center gap-3 pb-4">
                          <div className="rounded-lg bg-accent-100 p-2 dark:bg-accent-800">
                            <DollarSign className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-primary-600 dark:text-primary-400">
                              Total Obligated
                            </div>
                            <div className="text-xl font-bold text-primary-900 dark:text-primary-100">
                              {formatCurrency(primaryAwardData.totalObligated)}
                            </div>
                          </div>
                          <div className="ml-auto text-right">
                            <div className="text-sm font-medium text-primary-600 dark:text-primary-400">
                              Source
                            </div>
                            <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                              {primaryAwardData.source === 'usaspending'
                                ? 'USAspending.gov'
                                : 'SAM.gov'}
                            </div>
                          </div>
                        </div>
                      )}

                    {/* USAspending Linked Awards - primary when available */}
                    {hasUsaspendingAwards && linkedAwards && (
                      <div
                        className={
                          primaryAwardData?.totalObligated != null
                            ? 'space-y-4 border-t border-primary-200 pt-4 dark:border-primary-700'
                            : 'space-y-4'
                        }
                      >
                        {linkedAwards.awards.map((award) => (
                          <LinkedAwardCard key={award.id} award={award} />
                        ))}
                      </div>
                    )}

                    {/* SAM.gov Award Notices */}
                    {hasSamAwards && (
                      <div
                        className={
                          hasUsaspendingAwards
                            ? // Compact section when USAspending is primary
                              'mt-4 border-t border-primary-200 pt-3 dark:border-primary-700'
                            : primaryAwardData?.totalObligated != null
                              ? 'space-y-4 border-t border-primary-200 pt-4 dark:border-primary-700'
                              : 'space-y-4'
                        }
                      >
                        {hasUsaspendingAwards && (
                          <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-primary-400">
                            SAM.gov Award Notices
                          </div>
                        )}
                        <div
                          className={
                            hasUsaspendingAwards ? 'space-y-1' : 'space-y-4'
                          }
                        >
                          {data.awardNotices.map((award) => (
                            <SamAwardNoticeCard
                              key={award.id}
                              award={award}
                              compact={!!hasUsaspendingAwards}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Loading state */}
                    {isLoadingAwards &&
                      !linkedAwards &&
                      data.awardNotices.length === 0 && (
                        <div className="flex items-center justify-center py-8">
                          <Spinner className="h-6 w-6" />
                          <span className="ml-2 text-sm text-primary-500">
                            Loading award data from USASpending...
                          </span>
                        </div>
                      )}

                    {/* Empty state */}
                    {!isLoadingAwards &&
                      (!linkedAwards || linkedAwards.awardCount === 0) &&
                      data.awardNotices.length === 0 && (
                        <div className="py-8 text-center text-sm text-primary-500 dark:text-primary-400">
                          No award data available for this contract.
                        </div>
                      )}
                  </CardV2.Content>
                </CardV2>
              </TabPanelV2.Content>
            )}
          </TabPanelV2>

          {!isDocumentsTab ? (
            <>
              {/* Divider below tabs */}
              <div className="mt-4 border-t border-primary-200 dark:border-primary-700" />

              {/* Classification Codes Section */}
              {(data.codes.naicsCode || data.codes.pscCode) && (
                <CardV2 variant="accent" className="mt-3">
                  <CardV2.Header>
                    <CardV2.HeaderIcon>
                      <Tag />
                    </CardV2.HeaderIcon>
                    <CardV2.HeaderTitle>
                      Classification Codes
                    </CardV2.HeaderTitle>
                  </CardV2.Header>
                  <CardV2.Content>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {data.codes.naicsCode && (
                        <div>
                          <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                            NAICS Code
                          </span>
                          <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                            {data.codes.naicsCode}
                          </div>
                          {data.codes.naicsIndustry && (
                            <div className="mt-0.5 text-sm text-primary-500 dark:text-primary-400">
                              {data.codes.naicsIndustry}
                            </div>
                          )}
                        </div>
                      )}
                      {data.codes.pscCode && (
                        <div>
                          <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                            PSC Code
                          </span>
                          <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                            {data.codes.pscCode}
                          </div>
                          {data.codes.pscIndustry && (
                            <div className="mt-0.5 text-sm text-primary-500 dark:text-primary-400">
                              {data.codes.pscIndustry}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardV2.Content>
                </CardV2>
              )}

              {/* Documents Section */}
              <SolicitationDocumentsSection
                files={solicitationFiles}
                persistenceKey={data.latestNotice.federal_contract_id}
                isHighlighted={false}
                className="mt-4"
                onSelectFile={(file) =>
                  goToDocumentsTab(file.id, 'compact-file-click')
                }
              />

              {/* Related Opportunities Section - SEO optimized */}
              <SimilarOpportunitiesV2
                type="federal_contract"
                opportunities={similarOpportunities}
                isLoading={isSimilarLoading}
              >
                <SimilarOpportunitiesV2.Header>
                  Related Contract Opportunities
                </SimilarOpportunitiesV2.Header>
                {isSimilarLoading ? (
                  <SimilarOpportunitiesV2.Skeleton />
                ) : (
                  <SimilarOpportunitiesV2.Grid>
                    {similarOpportunities.map((opp) => (
                      <SimilarOpportunitiesV2.Card
                        key={opp.id}
                        opportunity={opp}
                      />
                    ))}
                  </SimilarOpportunitiesV2.Grid>
                )}
              </SimilarOpportunitiesV2>
            </>
          ) : null}
        </div>

        {/* Sidebar */}
        {!isDocumentsTab ? (
          <div className="space-y-4 lg:col-span-4">
            {/* View Documents Button - mt-3 aligns with tab text (tabs have py-3), hidden on mobile */}
            <ViewDocumentsButton
              fileCount={solicitationFiles.length}
              onClick={() => goToDocumentsTab(selectedSolicitationFileId)}
              className="mt-3 hidden lg:flex"
            />

            {/* Project Timeline Widget - Color-coded events */}
            <NoticesV2.SidebarTimeline title="Project Timeline">
              {/* Original posting */}
              {data.notices.length > 0 &&
              data.notices[data.notices.length - 1].posted_date ? (
                <NoticesV2.TimelineEvent
                  type="posted"
                  icon={CheckCircle2}
                  title="Original Solicitation Posted"
                  date={
                    formatDate(
                      data.notices[data.notices.length - 1].posted_date,
                    ) ?? ''
                  }
                  onClick={() =>
                    goToNotice(data.notices[data.notices.length - 1].id)
                  }
                />
              ) : null}
              {/* All amendments - using memoized list */}
              {amendments.map(({ notice, idx }, amendmentIdx) => {
                const isLatestAmendment = idx === 0
                const diffs = diffsByNoticeId.get(notice.id)
                const amendmentNum = amendmentIdx + 1

                return (
                  <NoticesV2.TimelineEvent
                    key={notice.id}
                    type={isLatestAmendment ? 'amended-latest' : 'amended'}
                    icon={Megaphone}
                    title={getAmendmentTitle(
                      isLatestAmendment,
                      amendmentNum,
                      diffs?.descriptionChanged ?? false,
                    )}
                    date={formatDate(notice.posted_date) ?? ''}
                    onClick={() => goToNotice(notice.id)}
                  />
                )
              })}
              {/* Deadline - highlight if changed */}
              {data.latestNotice.response_deadline ? (
                <NoticesV2.TimelineEvent
                  type="deadline"
                  title="Response Deadline"
                  date={formatDate(data.latestNotice.response_deadline) ?? ''}
                  highlight={deadlineHighlighted}
                />
              ) : null}
              {/* Award events from unified notices (SAM.gov + USAspending) */}
              {unifiedAwardNotices.map((award) =>
                award.awardDate ? (
                  <NoticesV2.TimelineEvent
                    key={award.id}
                    type="award"
                    title={
                      award.awardee ? (
                        <>
                          Awarded to{' '}
                          {award.recipientHash ? (
                            <RecipientLink
                              hash={award.recipientHash}
                              level={selectRecipientLevel(
                                award.recipientLevels,
                                award.parentUei,
                              )}
                              className="font-semibold hover:text-blue-400 hover:underline dark:hover:text-blue-400"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {award.awardee}
                            </RecipientLink>
                          ) : (
                            <span className="font-semibold">
                              {award.awardee}
                            </span>
                          )}
                        </>
                      ) : (
                        'Award'
                      )
                    }
                    date={formatDate(award.awardDate) ?? ''}
                    onClick={goToAwardsTab}
                  />
                ) : null,
              )}
              {/* Archive date if available */}
              {data.latestNotice.archive_date && (
                <NoticesV2.TimelineEvent
                  type="expiry"
                  title="Archive Date"
                  date={formatDate(data.latestNotice.archive_date) ?? ''}
                />
              )}
            </NoticesV2.SidebarTimeline>

            {/* Agency Info Widget */}
            {(data.stats.department ||
              data.stats.subTier ||
              data.stats.office) && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={Building2}>
                  Agency Information
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  {data.stats.department && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Department
                      </span>
                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                        {data.stats.department}
                      </div>
                    </div>
                  )}
                  {data.stats.subTier && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Sub-Tier
                      </span>
                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                        {data.stats.subTier}
                      </div>
                    </div>
                  )}
                  {data.stats.office && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Office
                      </span>
                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                        {data.stats.office}
                      </div>
                    </div>
                  )}
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Contact Info Widget */}
            {(data.contact.name ||
              data.contact.email ||
              data.contact.phone) && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={User}>
                  Point of Contact
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  {data.contact.name && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        {data.contact.title || 'Name'}
                      </span>
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5 text-primary-400" />
                        <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                          {data.contact.name}
                        </span>
                      </div>
                    </div>
                  )}
                  {data.contact.email && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Email
                      </span>
                      <a
                        href={`mailto:${data.contact.email}`}
                        className="group flex items-center gap-2 text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        <span className="truncate">{data.contact.email}</span>
                      </a>
                    </div>
                  )}
                  {data.contact.phone && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Phone
                      </span>
                      <a
                        href={`tel:${data.contact.phone}`}
                        className="group flex items-center gap-2 text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        <span>{data.contact.phone}</span>
                      </a>
                    </div>
                  )}
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Place of Performance Widget */}
            {data.placeOfPerformance.formatted && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={MapPin}>
                  Place of Performance
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                    {data.placeOfPerformance.formatted}
                  </div>
                  {data.placeOfPerformance.street && (
                    <div className="mt-1 text-sm text-primary-500 dark:text-primary-400">
                      {data.placeOfPerformance.street}
                    </div>
                  )}
                  {data.placeOfPerformance.zip && (
                    <div className="text-sm text-primary-500 dark:text-primary-400">
                      ZIP: {data.placeOfPerformance.zip}
                    </div>
                  )}
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Official Sources Widget */}
            {data.externalLink && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={ExternalLink}>
                  Official Sources
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  <a
                    href={data.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex w-full items-center justify-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-700 transition-all hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300 dark:hover:border-accent-600 dark:hover:bg-accent-900/30 dark:hover:text-accent-400"
                  >
                    View on SAM.gov
                    <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </a>
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}
          </div>
        ) : null}
      </div>
    </>
  )
}

ContractDisplayV2.displayName = 'ContractDisplayV2'

export { ContractDisplayV2 }
export type { ContractDisplayV2Props }
